import { useId, useState, type KeyboardEvent } from 'react';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { Input } from './Input';

interface ChipEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
  suggestions: string[];
  addLabel: string;
  tone: 'brand' | 'teal';
}

export function ChipEditor({ values, onChange, suggestions, addLabel, tone }: ChipEditorProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const add = (raw: string) => {
    const v = raw.trim();
    setDraft('');
    setOpen(false);
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
  };

  const available = suggestions.filter((s) => !values.includes(s));

  return (
    <div className="ds-tag-stack" style={{ flexWrap: 'wrap', gap: 5 }}>
      {values.length === 0 && !open && (
        <span style={{ color: 'var(--text-4)', fontSize: 12.5, fontStyle: 'italic' }}>None</span>
      )}
      {values.map((t) => (
        <span key={t} className={cn('ds-tag', tone)} style={{ height: 22, paddingRight: 4 }}>
          {t}
          <IconButton
            icon="x"
            iconSize={10}
            size="sm"
            label={`Remove ${t}`}
            style={{ width: 16, height: 16, marginLeft: 4, color: 'currentColor' }}
            onClick={() => onChange(values.filter((x) => x !== t))}
          />
        </span>
      ))}
      {open ? (
        <>
          <Input
            autoFocus
            list={available.length ? listId : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => (draft.trim() ? add(draft) : setOpen(false))}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add(draft);
              }
              if (e.key === 'Escape') {
                setDraft('');
                setOpen(false);
              }
            }}
            placeholder={`${addLabel}…`}
            style={{ width: 170, height: 22, fontSize: 11, padding: '0 8px' }}
          />
          {available.length > 0 && (
            <datalist id={listId}>
              {available.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          )}
        </>
      ) : (
        <button
          type="button"
          className="chip-btn"
          style={{ height: 22, fontSize: 11, padding: '0 6px' }}
          onClick={() => setOpen(true)}
        >
          <Icon name="plus" size={10} /> {addLabel}
        </button>
      )}
    </div>
  );
}
